import {
	Button,
	Card,
	CardContent,
	Grid2,
	MenuItem,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type {
	ColDef,
	GridReadyEvent,
	RowClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { ErrorModal } from "../../../components/modals/error-modal";
import { CenteredLazyLoading } from "../../../components/shared/CenteredLazyLoading";
import { themeDXT } from "../../../css/themeMESF";
import { HelmetDexteel } from "../../../helmet/HelmetDexteel";
import { DateFilter } from "../log/components/Filters/dateFilter";
import type {
	ClientTelemetry,
	ClientTelemetryEvent,
	ClientTelemetryFilters,
} from "./models/ClientTelemetry";
import {
	getClientTelemetry,
	getClientTelemetryEvents,
} from "./repositories/ClientTelemetryRepository";

const EVENT_TYPES = [
	"session_start",
	"heartbeat",
	"environment_change",
	"route_view",
	"visibility_visible",
	"visibility_hidden",
	"logout",
];

const getDefaultFilters = (): ClientTelemetryFilters => ({
	startDate: moment().add(-1, "days").hour(0).minute(0).second(0).toDate(),
	endDate: moment().hour(23).minute(59).second(59).toDate(),
	search: "",
	userId: null,
	eventType: "",
});

const formatDateTime = (value?: Date | null) => {
	return value ? moment(value).format("YYYY-MM-DD HH:mm:ss") : "";
};

const formatResolution = ({ ScreenWidth, ScreenHeight }: ClientTelemetry) => {
	return ScreenWidth && ScreenHeight ? `${ScreenWidth} x ${ScreenHeight}` : "";
};

const formatViewport = ({ ViewportWidth, ViewportHeight }: ClientTelemetry) => {
	return ViewportWidth && ViewportHeight
		? `${ViewportWidth} x ${ViewportHeight}`
		: "";
};

const useClientTelemetryQuery = (filters: ClientTelemetryFilters) => {
	return useQuery<ClientTelemetry[], Error>({
		queryKey: [
			"clientTelemetry",
			filters.startDate.getTime(),
			filters.endDate?.getTime(),
			filters.search,
			filters.userId,
		],
		queryFn: ({ signal }) => getClientTelemetry(filters, signal),
		keepPreviousData: true,
		staleTime: 10_000,
	});
};

const useClientTelemetryEventsQuery = (
	filters: ClientTelemetryFilters,
	selectedClientId?: string,
) => {
	return useQuery<ClientTelemetryEvent[], Error>({
		queryKey: [
			"clientTelemetryEvents",
			filters.startDate.getTime(),
			filters.endDate?.getTime(),
			filters.search,
			filters.userId,
			filters.eventType,
			selectedClientId,
		],
		queryFn: ({ signal }) =>
			getClientTelemetryEvents(
				{
					...filters,
					clientId: selectedClientId,
				},
				signal,
			),
		enabled: Boolean(selectedClientId),
		keepPreviousData: true,
		staleTime: 10_000,
	});
};

const ClientTelemetryPage = () => {
	const [draftFilters, setDraftFilters] = useState<ClientTelemetryFilters>(() =>
		getDefaultFilters(),
	);
	const [appliedFilters, setAppliedFilters] = useState<ClientTelemetryFilters>(
		() => getDefaultFilters(),
	);
	const [selectedClient, setSelectedClient] = useState<ClientTelemetry | null>(
		null,
	);
	const [error, setError] = useState("");

	const {
		data: clients,
		isLoading: isLoadingClients,
		isFetching: isFetchingClients,
		isError: isClientsError,
		error: clientsError,
	} = useClientTelemetryQuery(appliedFilters);

	const {
		data: events,
		isLoading: isLoadingEvents,
		isFetching: isFetchingEvents,
		isError: isEventsError,
		error: eventsError,
	} = useClientTelemetryEventsQuery(appliedFilters, selectedClient?.ClientId);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			filter: true,
			resizable: true,
			wrapHeaderText: true,
			suppressHeaderMenuButton: true,
		}),
		[],
	);

	const clientColumnDefs = useMemo<ColDef<ClientTelemetry>[]>(
		() => [
			{
				field: "LastSeenAt",
				headerName: "Last Seen",
				valueFormatter: ({ value }) => formatDateTime(value),
				sort: "desc",
				width: 170,
			},
			{ field: "Username", headerName: "User", width: 140 },
			{ field: "UserId", headerName: "User ID", width: 100 },
			{ field: "LastEventType", headerName: "Last Event", width: 150 },
			{ field: "BrowserName", headerName: "Browser", width: 130 },
			{ field: "BrowserVersion", headerName: "Version", width: 120 },
			{ field: "Platform", headerName: "Platform", width: 140 },
			{ field: "IpAddress", headerName: "IP Address", width: 140 },
			{ field: "LastPath", headerName: "Last Path", flex: 1, minWidth: 260 },
			{
				headerName: "Screen",
				valueGetter: ({ data }) => (data ? formatResolution(data) : ""),
				width: 120,
			},
			{
				headerName: "Viewport",
				valueGetter: ({ data }) => (data ? formatViewport(data) : ""),
				width: 120,
			},
			{ field: "Timezone", headerName: "Timezone", width: 170 },
			{ field: "ClientId", headerName: "Client ID", width: 280 },
			{ field: "CurrentSessionId", headerName: "Current Session", width: 280 },
		],
		[],
	);

	const eventColumnDefs = useMemo<ColDef<ClientTelemetryEvent>[]>(
		() => [
			{
				field: "EventTimestamp",
				headerName: "Timestamp",
				valueFormatter: ({ value }) => formatDateTime(value),
				sort: "desc",
				width: 170,
			},
			{ field: "EventType", headerName: "Event", width: 150 },
			{ field: "Username", headerName: "User", width: 140 },
			{ field: "Path", headerName: "Path", flex: 1, minWidth: 260 },
			{
				field: "PreviousPath",
				headerName: "Previous Path",
				flex: 1,
				minWidth: 220,
			},
			{ field: "VisibilityState", headerName: "Visibility", width: 120 },
			{ field: "BrowserName", headerName: "Browser", width: 130 },
			{ field: "Platform", headerName: "Platform", width: 140 },
			{ field: "IpAddress", headerName: "IP Address", width: 140 },
			{ field: "SessionId", headerName: "Session ID", width: 280 },
		],
		[],
	);

	const handleApplyFilters = () => {
		setAppliedFilters({ ...draftFilters });
	};

	const handleResetFilters = () => {
		const filters = getDefaultFilters();
		setDraftFilters(filters);
		setAppliedFilters(filters);
		setSelectedClient(null);
	};

	const handleClientClicked = (event: RowClickedEvent<ClientTelemetry>) => {
		setSelectedClient(event.data ?? null);
	};

	const handleClientGridReady = (event: GridReadyEvent) => {
		event.api.sizeColumnsToFit();
	};

	useEffect(() => {
		if (isClientsError && clientsError) {
			setError(clientsError.message);
		}
	}, [isClientsError, clientsError]);

	useEffect(() => {
		if (isEventsError && eventsError) {
			setError(eventsError.message);
		}
	}, [isEventsError, eventsError]);

	return (
		<>
			<HelmetDexteel title="Client Telemetry" />
			<Grid2 container justifyContent="flex-start" p={1} spacing={2}>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Client Telemetry
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Admin view of browser clients and captured telemetry events.
					</Typography>
				</Grid2>

				<Grid2 size={{ md: 12, xs: 12 }}>
					<Card>
						<CardContent>
							<Grid2 container alignItems="center" spacing={2}>
								<Grid2 size={{ md: 1.7, xs: 12 }}>
									<DateFilter
										label="From"
										date={draftFilters.startDate}
										setDate={(date: Date | null) =>
											setDraftFilters({
												...draftFilters,
												startDate: date || new Date(),
											})
										}
										maxDate={draftFilters.endDate ?? undefined}
									/>
								</Grid2>
								<Grid2 size={{ md: 1.7, xs: 12 }}>
									<DateFilter
										label="To"
										date={draftFilters.endDate}
										setDate={(date: Date | null) =>
											setDraftFilters({ ...draftFilters, endDate: date })
										}
										minDate={draftFilters.startDate}
									/>
								</Grid2>
								<Grid2 size={{ md: 2.8, xs: 12 }}>
									<TextField
										fullWidth
										label="Search"
										size="small"
										value={draftFilters.search}
										onChange={(event) =>
											setDraftFilters({
												...draftFilters,
												search: event.target.value,
											})
										}
									/>
								</Grid2>
								<Grid2 size={{ md: 1.2, xs: 12 }}>
									<TextField
										fullWidth
										label="User ID"
										size="small"
										type="number"
										value={draftFilters.userId ?? ""}
										onChange={(event) =>
											setDraftFilters({
												...draftFilters,
												userId: event.target.value
													? Number(event.target.value)
													: null,
											})
										}
									/>
								</Grid2>
								<Grid2 size={{ md: 1.8, xs: 12 }}>
									<TextField
										select
										fullWidth
										label="Event Type"
										size="small"
										value={draftFilters.eventType}
										onChange={(event) =>
											setDraftFilters({
												...draftFilters,
												eventType: event.target.value,
											})
										}
									>
										<MenuItem value="">All</MenuItem>
										{EVENT_TYPES.map((eventType) => (
											<MenuItem key={eventType} value={eventType}>
												{eventType}
											</MenuItem>
										))}
									</TextField>
								</Grid2>
								<Grid2 size={{ md: 1.4, xs: 12 }}>
									<Button
										fullWidth
										variant="contained"
										color="primary"
										onClick={handleApplyFilters}
									>
										Apply
									</Button>
								</Grid2>
								<Grid2 size={{ md: 1.4, xs: 12 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={handleResetFilters}
									>
										Reset
									</Button>
								</Grid2>
							</Grid2>
						</CardContent>
					</Card>
				</Grid2>

				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="subtitle1" fontWeight={600} gutterBottom>
						Clients
					</Typography>
					<Paper style={{ height: "38vh", width: "100%" }}>
						<AgGridReact
							loading={isLoadingClients || isFetchingClients}
							gridOptions={{ theme: themeDXT }}
							rowData={clients}
							columnDefs={clientColumnDefs}
							defaultColDef={defaultColDef}
							rowHeight={34}
							headerHeight={40}
							animateRows
							loadingOverlayComponent={CenteredLazyLoading}
							rowSelection="single"
							getRowId={(params) => params.data.ClientId}
							onGridReady={handleClientGridReady}
							onRowClicked={handleClientClicked}
						/>
					</Paper>
				</Grid2>

				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="subtitle1" fontWeight={600} gutterBottom>
						Events{selectedClient ? ` for ${selectedClient.ClientId}` : ""}
					</Typography>
					<Paper style={{ height: "32vh", width: "100%" }}>
						<AgGridReact
							loading={
								Boolean(selectedClient) && (isLoadingEvents || isFetchingEvents)
							}
							gridOptions={{ theme: themeDXT }}
							rowData={selectedClient ? events : []}
							columnDefs={eventColumnDefs}
							defaultColDef={defaultColDef}
							rowHeight={34}
							headerHeight={40}
							animateRows
							loadingOverlayComponent={CenteredLazyLoading}
							getRowId={(params) => String(params.data.ClientTelemetryEventId)}
						/>
					</Paper>
					{!selectedClient && (
						<Typography variant="body2" color="text.secondary" mt={1}>
							Select a client row to load its events.
						</Typography>
					)}
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};

export default ClientTelemetryPage;
